import { useLocation, useRouteLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useEditPermissions } from '~/modules/user';
import { getSubRoutes } from '@modules/professional/component/sub-route-navigation/utils';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import { getActiveTabLink } from '~/ui-rocco/pages/professional-detail/utils/subnav-active';
import { buildProfessionalNavItems } from '~/ui-rocco/pages/professional-detail/utils/professional-nav-items';
import { ProfessionalContactTabButton } from './ProfessionalContactTabButton';

/** Match the Figma's tab labels (legacy uses Profile). */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
    Profile: 'Overview',
};

/**
 * Compact tab bar below the profile header — the professional's available tabs
 * (Overview / About / Projects / Products / Articles / Network / Contact …) as
 * a single full-bleed row that scrolls horizontally, the active tab marked by a
 * weight change and an underline (modern marketplace-profile style rather than
 * the old pill bar). Reuses {@link getSubRoutes} so only real tabs show. Lives
 * in the persistent layout so it renders once and never re-flashes on tab nav.
 */
export const ProfessionalSubNav = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const { pathname } = useLocation();
    const imageLabelSearchEnabled = useFeature('image_label_search_profile');
    const initial = layoutData?.initial;
    // Editors with Products allowed but no products yet still need the Products
    // tab to add their first product — mirror the legacy nav's edit permission.
    const { canEditProducts } = useEditPermissions(initial?.ID ?? 0);
    if (!initial) return null;

    const base = `/professional/${initial.URLSegment}`;
    const currentPath = pathname.replace(/\/+$/, '');
    const professional = initial as unknown as ProfessionalInitialResult;
    // Match prod's tab set: pass linked-profile locations (Contact) and the
    // image-label flag (Spaces) through, exactly as the legacy nav does.
    const items = getSubRoutes(
        professional,
        canEditProducts,
        layoutData?.linkedProfiles?.linkedProfilesList,
        imageLabelSearchEnabled,
    );
    const navItems = buildProfessionalNavItems(items, {
        hasDownloads: professional.HasDownloads,
    });

    const tabLinks = navItems.map((item) =>
        `${base}${item.link}`.replace(/\/+$/, ''),
    );
    const activeTabLink = getActiveTabLink(tabLinks, currentPath);

    return (
        <nav className="mt-6 border-b border-black/10 bg-white md:mt-9">
            {/* overflow-y-clip is load-bearing: overflow-x-auto alone coerces
                the y axis from visible to auto, making the bar a vertical
                scroll container that any sub-pixel overflow lets mobile wiggle
                and bounce. clip clips without ever scrolling. */}
            <div className="mx-auto flex w-full max-w-[120rem] [scrollbar-width:none] items-center gap-6 overflow-x-auto overflow-y-clip px-6 md:gap-8 md:px-[3.125rem] [&::-webkit-scrollbar]:hidden">
                {navItems.map((item) => {
                    const to = `${base}${item.link}`.replace(/\/+$/, '');
                    const isActive = to === activeTabLink;
                    const tabClassName = cn(
                        'relative shrink-0 py-4 font-sans whitespace-nowrap transition-colors max-md:text-[0.9375rem] md:text-base',
                        isActive
                            ? 'font-medium text-[#181818]'
                            : 'text-[#8a8a8a] hover:text-[#181818]',
                    );
                    if (item.opensEnquiry) {
                        return (
                            <ProfessionalContactTabButton
                                key={item.link}
                                className={tabClassName}
                            />
                        );
                    }
                    return (
                        <RoccoLink
                            key={item.link}
                            to={to}
                            // "intent", not "render" — see the desktop pill
                            // nav: nine rendered prefetches each re-run the
                            // overview-merged tab loader.
                            prefetch="intent"
                            preventScrollReset
                            className={tabClassName}
                        >
                            {LABEL_OVERRIDES[item.label] ?? item.label}
                            {/* bottom-0 (not -bottom-px): the underline must
                                not poke past the scroller's bottom edge — that
                                1px of overflow is what made the bar vertically
                                scrollable. It sits flush above the nav's
                                hairline border instead. */}
                            {isActive ? (
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-[#181818]"
                                />
                            ) : null}
                        </RoccoLink>
                    );
                })}
            </div>
        </nav>
    );
};
